import React, { useEffect, useState } from "react";
import { Alert, Modal, Spinner } from "flowbite-react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import {
  HiOutlineExclamation,
  HiOutlineEye,
  HiOutlineEyeOff,
  HiOutlineInformationCircle,
} from "react-icons/hi";

import Button from "../../components/Button";
import SwitchButton from "../../components/SwitchButton";
import env from "../../../environments";
import {
  getDashboardVisibility,
  getDashboardVisibilityRefusal,
  putDashboardVisibility,
} from "../../api/memberDashboardVisibility/actions";
import { memberDashboardVisibilityQueryKeys } from "../../api/memberDashboardVisibility/queryKeys";
import {
  DASHBOARD_VISIBILITY_CUSTOMER_NOT_FOUND,
  DASHBOARD_VISIBILITY_OPERATOR_UNVERIFIED,
  DashboardSection,
  DashboardVisibilityRefusal,
  DashboardVisibilityResponse,
  DashboardVisibilityUpdate,
  Visibility,
} from "../../api/memberDashboardVisibility/types";
import { useOperatorIdentity } from "../../hooks/useOperatorIdentity";
import { useOperatorToken } from "../../hooks/useOperatorToken";
import { formatDateTime5 } from "../../../utils/date";
import {
  memberAccessStrings,
  formatMessage,
} from "./MemberAccessTab.strings";

const S = memberAccessStrings.memberApp;

/** Row order of the "Member app sections" list. */
const SECTIONS: DashboardSection[] = ["books", "magazines"];

/** The operator's unsaved choices: only sections whose value differs. */
type Draft = Partial<Record<DashboardSection, Visibility>>;

const flip = (value: Visibility): Visibility =>
  value === "shown" ? "hidden" : "shown";

/** The sections the draft really changes against the server state. */
const changedSections = (
  draft: Draft,
  data: DashboardVisibilityResponse | undefined
): DashboardSection[] =>
  data
    ? SECTIONS.filter(
        (section) =>
          draft[section] !== undefined &&
          draft[section] !== data[section].effective
      )
    : [];

const formatChangeDate = (value: string): string | null => {
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : formatDateTime5(date);
};

/**
 * The line under a row's chip: who set the value, or that nothing is set
 * and the legacy flags decide.
 */
const sourceLine = (
  section: DashboardSection,
  data: DashboardVisibilityResponse
): string => {
  const state = data[section];
  if (state.stored === null) {
    return S.notSet;
  }
  const change = data.lastChange;
  const date = change ? formatChangeDate(change.at) : null;
  if (change && change.field === section && change.to === state.stored && date) {
    return formatMessage(S.lastChange, {
      value: S.values[change.to],
      operator: change.operator,
      date,
    });
  }
  return S.setByOperator;
};

const StatusChip: React.FC<{ value: Visibility; isSet: boolean }> = ({
  value,
  isSet,
}) => {
  const Icon = value === "shown" ? HiOutlineEye : HiOutlineEyeOff;
  const tone = !isSet
    ? "border-dashed border-neutral-300 bg-white text-neutral-700"
    : value === "shown"
    ? "border-emerald-50 bg-emerald-50 text-emerald-800"
    : "border-neutral-200 bg-neutral-100 text-neutral-800";
  return (
    <span
      className={`inline-flex h-[26px] w-fit items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 text-sm font-semibold ${tone}`}
      data-testid="access-status-chip"
    >
      <Icon className="h-4 w-4" aria-hidden="true" />
      {S.values[value]}
    </span>
  );
};

interface SectionRowProps {
  section: DashboardSection;
  customerId: string;
  data: DashboardVisibilityResponse;
  draftValue: Visibility;
  unsaved: boolean;
  disabled: boolean;
  onToggle: (section: DashboardSection) => void;
  onSet: (section: DashboardSection, value: Visibility) => void;
}

/**
 * The "Hidden" / "Shown" words beside the switch. A click sets the value it
 * names (a no-op when it is already set) instead of toggling, so clicking
 * "Hidden" on a hidden row never queues a change. Pointer-only and hidden
 * from assistive tech: the switch is the control and carries the name.
 */
const SwitchSideLabel: React.FC<{
  value: Visibility;
  disabled: boolean;
  onSet: (value: Visibility) => void;
}> = ({ value, disabled, onSet }) => (
  <span
    aria-hidden="true"
    className={`select-none text-sm font-semibold ${
      disabled
        ? "cursor-not-allowed text-neutral-400"
        : "cursor-pointer text-neutral-700"
    }`}
    data-testid={`access-side-label-${value}`}
    onClick={() => {
      if (!disabled) {
        onSet(value);
      }
    }}
  >
    {S.values[value]}
  </span>
);

/** One row: name → status chip → Hidden | Shown switch (option C). */
const SectionRow: React.FC<SectionRowProps> = ({
  section,
  customerId,
  data,
  draftValue,
  unsaved,
  disabled,
  onToggle,
  onSet,
}) => {
  const copy = S.sections[section];
  const state = data[section];
  const switchId = `access-${section}-${customerId}`;
  const setTo = (value: Visibility) => onSet(section, value);
  return (
    <li
      className="flex flex-wrap items-start gap-x-6 gap-y-3 border-t border-neutral-100 py-4"
      data-testid={`access-row-${section}`}
    >
      <div className="flex min-w-0 flex-[1_1_240px] flex-col gap-1">
        <p className="text-[15px] font-bold text-neutral-900">{copy.name}</p>
        <p className="text-sm text-neutral-600">{copy.description}</p>
      </div>
      <div className="flex min-w-0 flex-[1_1_220px] flex-col gap-1">
        <StatusChip value={state.effective} isSet={state.stored !== null} />
        <p
          className="text-sm text-neutral-600"
          data-testid={`access-source-${section}`}
        >
          {sourceLine(section, data)}
        </p>
      </div>
      <div className="flex min-h-[40px] items-center gap-2">
        <SwitchSideLabel value="hidden" disabled={disabled} onSet={setTo} />
        <SwitchButton
          id={switchId}
          checked={draftValue === "shown"}
          setChecked={() => onToggle(section)}
          disabled={disabled}
          hideCheckIcon
          ariaLabel={formatMessage(S.switchLabel, { section: copy.name })}
        />
        <SwitchSideLabel value="shown" disabled={disabled} onSet={setTo} />
        {unsaved && (
          <span
            className="rounded bg-amber-100 px-2 py-0.5 text-xs font-bold text-amber-800"
            data-testid={`access-unsaved-${section}`}
          >
            {S.unsaved}
          </span>
        )}
      </div>
    </li>
  );
};

const refusalMessage = (refusal: DashboardVisibilityRefusal): string =>
  S.refusals[refusal.code];

interface MemberAppSectionsProps {
  customerId: string;
  /** Shown in the confirmation title ("Save 2 changes for Jane Doe?"). */
  memberName: string;
  /**
   * `false` until the operator opens the Access tab: flowbite mounts every
   * tab panel with the page, so the GET waits for this instead.
   */
  active?: boolean;
}

/**
 * "Member app sections" of the Access tab (abo-hide-books-rm, ADR-005 option
 * C): one row per member app section with its effective value, a local
 * draft edited by the switches, a save bar while the draft differs, and a
 * confirmation that lists every change. Confirm reads the operator's
 * FusionAuth token at that moment (ADR-004) and PUTs only the changed
 * sections; the token is never kept in state, cache or logs.
 */
export const MemberAppSections: React.FC<MemberAppSectionsProps> = ({
  customerId,
  memberName,
  active = true,
}) => {
  const queryClient = useQueryClient();
  const readOperator = useOperatorIdentity();
  const { getOperatorToken, signInAgain } = useOperatorToken();
  const detailKey = memberDashboardVisibilityQueryKeys.detail(customerId);

  const [draft, setDraft] = useState<Draft>({});
  const [confirming, setConfirming] = useState(false);
  const [refusal, setRefusal] = useState<DashboardVisibilityRefusal | null>(
    null
  );

  useEffect(() => {
    setDraft({});
    setConfirming(false);
    setRefusal(null);
  }, [customerId]);

  const query = useQuery(detailKey, () => getDashboardVisibility(customerId), {
    enabled: Boolean(customerId) && active,
    refetchOnWindowFocus: false,
    // A failure shows the load-error notice at once; the operator retries.
    retry: false,
  });

  const mutation = useMutation(
    async (body: DashboardVisibilityUpdate) => {
      // Read at Confirm time, never earlier, and handed to this one request.
      const token = await getOperatorToken();
      return putDashboardVisibility(customerId, body, {
        ...readOperator(),
        token,
      });
    },
    {
      onSuccess: (response) => {
        queryClient.setQueryData(detailKey, response);
        void queryClient.invalidateQueries(detailKey);
        setDraft({});
        setConfirming(false);
        setRefusal(null);
      },
      onError: (error: unknown) => {
        setRefusal(getDashboardVisibilityRefusal(error));
      },
    }
  );

  const data = query.data;
  const changes = changedSections(draft, data);
  const saving = mutation.isLoading;

  // Idle: the tab has not been opened yet, so nothing was requested.
  if (query.isIdle || query.isLoading) {
    return (
      <div className="flex justify-center py-8" data-testid="access-loading">
        <Spinner size="lg" />
      </div>
    );
  }

  if (query.isError || !data) {
    const loadRefusal = getDashboardVisibilityRefusal(query.error);
    return (
      <Alert color="failure" data-testid="access-load-error">
        <div className="flex flex-col items-start gap-2">
          <span>
            {loadRefusal.code === DASHBOARD_VISIBILITY_CUSTOMER_NOT_FOUND
              ? S.notFound
              : S.loadError}
          </span>
          <Button variant="LIGHT" onClick={() => void query.refetch()}>
            {S.retry}
          </Button>
        </div>
      </Alert>
    );
  }

  const valueOf = (section: DashboardSection): Visibility =>
    changes.includes(section)
      ? (draft[section] as Visibility)
      : data[section].effective;

  /** Puts `value` in the draft, or drops the entry when it is the saved one. */
  const setValue = (section: DashboardSection, value: Visibility) => {
    if (value === valueOf(section)) {
      return;
    }
    setDraft((current) => {
      const updated = { ...current };
      if (value === data[section].effective) {
        delete updated[section];
      } else {
        updated[section] = value;
      }
      return updated;
    });
  };

  const toggle = (section: DashboardSection) =>
    setValue(section, flip(valueOf(section)));

  const openConfirm = () => {
    setRefusal(null);
    setConfirming(true);
  };

  const closeConfirm = () => {
    if (saving) {
      return;
    }
    setConfirming(false);
    setRefusal(null);
  };

  const confirm = () => {
    if (saving || changes.length === 0) {
      return;
    }
    const body: DashboardVisibilityUpdate = {};
    changes.forEach((section) => {
      body[section] = draft[section];
    });
    setRefusal(null);
    mutation.mutate(body);
  };

  const count = changes.length;
  const plural = count === 1 ? "one" : "other";

  return (
    <section
      className="flex flex-col gap-4"
      aria-labelledby={`access-member-app-${customerId}`}
      data-testid="access-member-app-sections"
    >
      <div className="flex flex-col gap-1">
        <h3
          id={`access-member-app-${customerId}`}
          className="text-base font-semibold leading-tight text-zinc-900"
        >
          {S.heading}
        </h3>
        <p className="text-sm text-neutral-600">{S.description}</p>
      </div>

      <ul>
        {SECTIONS.map((section) => (
          <SectionRow
            key={section}
            section={section}
            customerId={customerId}
            data={data}
            draftValue={valueOf(section)}
            unsaved={changes.includes(section)}
            disabled={saving}
            onToggle={toggle}
            onSet={setValue}
          />
        ))}
      </ul>

      <div className="flex flex-col gap-2 border-t border-neutral-100 pt-4 text-sm">
        <p
          className="flex items-center gap-2 text-neutral-800"
          data-testid="access-subscription-hint"
        >
          <HiOutlineInformationCircle
            className="h-4 w-4 shrink-0 text-primary-700"
            aria-hidden="true"
          />
          {formatMessage(S.hint, { hint: S.hints[data.subscriptionHint] })}
        </p>
        {data.targetCustomerId !== data.customerId && (
          <p
            className="flex items-center gap-2 text-neutral-800"
            data-testid="access-case-variant"
          >
            <HiOutlineInformationCircle
              className="h-4 w-4 shrink-0 text-primary-700"
              aria-hidden="true"
            />
            {formatMessage(S.caseVariant, { targetId: data.targetCustomerId })}
          </p>
        )}
        <ul className="list-disc pl-5 text-neutral-600">
          <li>{S.notes.nextSignIn}</li>
          <li>{S.notes.bookAccess}</li>
        </ul>
      </div>

      {count > 0 && (
        <div
          className="sticky bottom-4 z-10 flex flex-wrap items-center justify-between gap-3 rounded-lg border border-neutral-200 bg-neutral-50 px-4 py-3 shadow-sm"
          data-testid="access-save-bar"
        >
          <p className="text-sm font-semibold text-neutral-900">
            {formatMessage(S.saveBar[plural], { count })}
          </p>
          <div className="flex gap-2">
            <Button
              variant="LIGHT"
              disabled={saving}
              onClick={() => setDraft({})}
            >
              {S.saveBar.discard}
            </Button>
            <Button variant="PRIMARY" disabled={saving} onClick={openConfirm}>
              {S.saveBar.save}
            </Button>
          </div>
        </div>
      )}

      <Modal show={confirming && count > 0} size="md" onClose={closeConfirm}>
        <Modal.Header>
          {formatMessage(
            plural === "one" ? S.dialog.titleOne : S.dialog.titleOther,
            { count, name: memberName }
          )}
        </Modal.Header>
        <Modal.Body>
          <div className="flex flex-col gap-3" data-testid="access-confirm">
            <ul className="flex flex-col gap-2">
              {changes.map((section) => (
                <li
                  key={section}
                  className="rounded-lg border border-neutral-200 px-3 py-2.5 text-sm font-semibold text-neutral-900"
                  data-testid="access-confirm-change"
                >
                  {formatMessage(S.dialog.change, {
                    section: S.sections[section].name,
                    from: S.values[data[section].effective],
                    to: S.values[draft[section] as Visibility],
                  })}
                </li>
              ))}
            </ul>
            <p className="text-sm text-neutral-600">{S.notes.nextSignIn}</p>
            <p className="text-sm text-neutral-600">{S.dialog.body}</p>
            {refusal && (
              <div
                className="flex items-start gap-2 rounded-lg bg-red-50 p-3"
                role="alert"
                data-testid="access-refusal"
                data-code={refusal.code}
              >
                <HiOutlineExclamation
                  className="mt-0.5 h-4 w-4 shrink-0 text-red-700"
                  aria-hidden="true"
                />
                <div className="flex flex-col items-start gap-2">
                  <p className="text-sm text-red-800">
                    {refusalMessage(refusal)}
                  </p>
                  {refusal.code === DASHBOARD_VISIBILITY_OPERATOR_UNVERIFIED && (
                    <Button variant="DARK" onClick={signInAgain}>
                      {S.dialog.signInAgain}
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="PRIMARY"
            isProcessing={saving}
            disabled={saving}
            onClick={confirm}
          >
            {S.dialog.confirm}
          </Button>
          <Button variant="LIGHT" disabled={saving} onClick={closeConfirm}>
            {S.dialog.cancel}
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

interface MemberAccessTabProps {
  customerId: string;
  memberName: string;
  /** `false` until the operator opens the tab (see MemberAppSections). */
  active?: boolean;
}

/**
 * Access tab of Customer Information (ADR-005): a vertical stack of
 * sections, each gated by its own flag, so the dmp-access-control
 * suspend/goodwill section can be added above or below without touching
 * this one. Today it holds "Member app sections" (ENABLE_DASHBOARD_VISIBILITY).
 */
const MemberAccessTab: React.FC<MemberAccessTabProps> = ({
  customerId,
  memberName,
  active = true,
}) => (
  <div className="flex flex-col gap-8" data-testid="member-access-tab">
    {env.ENABLE_DASHBOARD_VISIBILITY && (
      <MemberAppSections
        customerId={customerId}
        memberName={memberName}
        active={active}
      />
    )}
  </div>
);

export default MemberAccessTab;
